import React, { useState } from 'react';
import '../App.css';

function BluetoothControl({ bluetoothDevice, setBluetoothDevice, sendCommand }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');

  const connectBluetooth = async () => {
    setIsConnecting(true);
    try {
      // Request Bluetooth device - Arduino Nano BLE 33
      // Try with filters first, then fallback to acceptAllDevices
      let device;
      try {
        device = await navigator.bluetooth.requestDevice({
          filters: [
            { name: 'HeadPoseControl' }, // Arduino Nano BLE 33 device name
            { namePrefix: 'HeadPose' }   // Fallback for variations
          ],
          optionalServices: [
            '19b10000-e8f2-537e-4f6c-d104768a1214', // Custom service UUID
            '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
            '00001801-0000-1000-8000-00805f9b34fb'  // Generic Attribute
          ]
        });
      } catch (filterError) {
        // If filter fails, try acceptAllDevices
        console.log('Filter failed, trying acceptAllDevices...', filterError);
        device = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: [
            '19b10000-e8f2-537e-4f6c-d104768a1214', // Custom service UUID
            '00001800-0000-1000-8000-00805f9b34fb', // Generic Access
            '00001801-0000-1000-8000-00805f9b34fb'  // Generic Attribute
          ]
        });
      }

      device.addEventListener('gattserverdisconnected', () => {
        setConnectionStatus('Disconnected');
        setBluetoothDevice(null);
      });

      // Connect to GATT server
      const server = await device.gatt.connect();
      
      // Verify we can access the service
      try {
        const serviceUUID = '19b10000-e8f2-537e-4f6c-d104768a1214';
        const service = await server.getPrimaryService(serviceUUID);
        console.log('Service found:', serviceUUID);
        
        const characteristicUUID = '19b10001-e8f2-537e-4f6c-d104768a1214';
        const characteristic = await service.getCharacteristic(characteristicUUID);
        console.log('Characteristic found:', characteristicUUID);
      } catch (serviceError) {
        console.warn('Service/characteristic access warning:', serviceError);
        // Continue anyway - might still work
      }
      
      setBluetoothDevice(device);
      setConnectionStatus('Connected');
      
      console.log('Bluetooth connected:', device.name || device.id);
      console.log('Device address:', device.id);
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      setConnectionStatus('Connection Failed');
      alert('Failed to connect to Bluetooth device. Make sure it is paired and in range.');
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectBluetooth = async () => {
    if (bluetoothDevice && bluetoothDevice.gatt.connected) {
      bluetoothDevice.gatt.disconnect();
      setBluetoothDevice(null);
      setConnectionStatus('Disconnected');
    }
  };

  const testConnection = async () => {
    if (!bluetoothDevice || !bluetoothDevice.gatt.connected) {
      alert('Please connect to Bluetooth device first');
      return;
    }
    
    try {
      await sendCommand('pan 90');
      await new Promise(resolve => setTimeout(resolve, 500));
      await sendCommand('tilt 90');
      alert('Test commands sent successfully!');
    } catch (error) {
      console.error('Test failed:', error);
      alert('Test failed. Check connection.');
    }
  };

  return (
    <div className="card">
      <h2>📡 Bluetooth Control</h2>
      
      <div className="input-group">
        <label>Status</label>
        <div className={`value-display ${bluetoothDevice?.gatt?.connected ? 'connected' : 'disconnected'}`} style={{ fontSize: '1.2em' }}>
          {bluetoothDevice?.gatt?.connected ? '🟢 Connected' : '🔴 Disconnected'}
        </div>
      </div>

      {bluetoothDevice && (
        <div className="input-group">
          <label>Device Info</label>
          <div style={{ padding: '10px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
            <div><strong>Name:</strong> {bluetoothDevice.name || 'Unknown'}</div>
            <div><strong>ID:</strong> {bluetoothDevice.id}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {!bluetoothDevice?.gatt?.connected ? (
          <button 
            className="button success" 
            onClick={connectBluetooth}
            disabled={isConnecting}
          >
            {isConnecting ? 'Connecting...' : 'Connect Bluetooth'}
          </button>
        ) : (
          <>
            <button 
              className="button danger" 
              onClick={disconnectBluetooth}
            >
              Disconnect
            </button>
            <button 
              className="button" 
              onClick={testConnection}
            >
              Test Connection
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(255, 193, 7, 0.2)', borderRadius: '10px' }}>
        <p style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
          <strong>Note:</strong> Make sure your Arduino Nano BLE 33 is powered on and advertising. 
          The device should appear as "HeadPoseControl" in the Bluetooth device list.
          <br /><br />
          <strong>Troubleshooting:</strong>
          <br />• Check Serial Monitor - should show "BLE device is now advertising"
          <br />• Make sure you're using Chrome or Edge browser
          <br />• Try refreshing the page if connection fails
          <br />• The app will show all available BLE devices if "HeadPoseControl" isn't found
        </p>
      </div>
    </div>
  );
}

export default BluetoothControl;
